import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import glopicApi from '@/lib/glopic-api'

interface BehaviorItem {
  id: number
  user_id: string | null
  event_name: string
  event_label: string
  properties: Record<string, string | number | boolean | null>
  device_id: string | null
  platform: string | null
  timestamp: string | null
}

interface ListResponse {
  items: BehaviorItem[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

const PAGE_SIZE = 20

const EVENT_OPTIONS = [
  { value: '', label: '全部事件' },
  { value: 'page_view', label: '页面访问' },
  { value: 'login_success', label: '登录成功' },
  { value: 'generate_start', label: '发起生成' },
  { value: 'generate_success', label: '生成成功' },
  { value: 'generate_fail', label: '生成失败' },
  { value: 'inspiration_open', label: '打开灵感' },
  { value: 'share_open', label: '打开分享' },
  { value: 'vip_upgrade_click', label: '点击升级VIP' },
]

const PLATFORM_ICON: Record<string, string> = {
  ios: 'ri-apple-line',
  android: 'ri-android-line',
  web: 'ri-global-line',
}

function formatProps(props: Record<string, string | number | boolean | null>): string {
  return Object.entries(props)
    .filter(([, v]) => v !== null && v !== undefined)
    .map(([k, v]) => `${k}: ${v}`)
    .join('  |  ')
}

function formatTime(iso: string | null): string {
  if (!iso) return '-'
  return new Date(iso).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function eventBadgeClass(eventName: string): string {
  if (eventName === 'page_view') return 'bg-blue-100 text-blue-700'
  if (eventName.startsWith('generate')) return 'bg-green-100 text-green-700'
  if (eventName === 'login_success') return 'bg-purple-100 text-purple-700'
  if (eventName === 'vip_upgrade_click') return 'bg-yellow-100 text-yellow-700'
  return 'bg-gray-100 text-gray-600'
}

export default function AiUserBehaviorPage(): ReactNode {
  const navigate = useNavigate()

  const [items, setItems] = useState<BehaviorItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const [filterUserId, setFilterUserId] = useState('')
  const [filterEvent, setFilterEvent] = useState('')
  const [filterFrom, setFilterFrom] = useState('')
  const [filterTo, setFilterTo] = useState('')

  const fetchData = useCallback(
    async (p: number, uid: string, ev: string, from: string, to: string) => {
      setLoading(true)
      try {
        const params: Record<string, string> = { page: String(p), page_size: String(PAGE_SIZE) }
        if (uid) params['user_id'] = uid
        if (ev) params['event_name'] = ev
        if (from) params['from_time'] = from
        if (to) params['to_time'] = to

        const res = await glopicApi.get<{ success: boolean; data: ListResponse }>('/behavior', { params })
        if (res.data.success) {
          setItems(res.data.data.items)
          setTotal(res.data.data.total)
          setPage(p)
        }
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    fetchData(1, '', '', '', '')
  }, [fetchData])

  const handleSearch = () => fetchData(1, filterUserId, filterEvent, filterFrom, filterTo)
  const handleReset = () => {
    setFilterUserId('')
    setFilterEvent('')
    setFilterFrom('')
    setFilterTo('')
    fetchData(1, '', '', '', '')
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-text-primary">用户行为</h2>
        <span className="text-sm text-text-secondary">共 {total.toLocaleString()} 条记录</span>
      </div>

      {/* Filters */}
      <div className="bg-bg-card rounded-xl p-4 flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-text-secondary">用户ID</label>
          <input
            className="border border-border rounded-lg px-3 py-2 text-sm w-40 bg-bg-base"
            placeholder="输入用户ID"
            value={filterUserId}
            onChange={e => setFilterUserId(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-text-secondary">事件类型</label>
          <select
            className="border border-border rounded-lg px-3 py-2 text-sm w-40 bg-bg-base"
            value={filterEvent}
            onChange={e => setFilterEvent(e.target.value)}
          >
            {EVENT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-text-secondary">开始时间</label>
          <input
            type="datetime-local"
            className="border border-border rounded-lg px-3 py-2 text-sm bg-bg-base"
            value={filterFrom}
            onChange={e => setFilterFrom(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-text-secondary">结束时间</label>
          <input
            type="datetime-local"
            className="border border-border rounded-lg px-3 py-2 text-sm bg-bg-base"
            value={filterTo}
            onChange={e => setFilterTo(e.target.value)}
          />
        </div>
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:opacity-90"
        >
          搜索
        </button>
        <button
          onClick={handleReset}
          className="px-4 py-2 border border-border rounded-lg text-sm hover:bg-bg-hover"
        >
          重置
        </button>
      </div>

      {/* Table */}
      <div className="bg-bg-card rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-text-secondary">加载中…</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-text-secondary text-left">
                <th className="px-4 py-3 font-medium">用户ID</th>
                <th className="px-4 py-3 font-medium">事件</th>
                <th className="px-4 py-3 font-medium">详情</th>
                <th className="px-4 py-3 font-medium">平台</th>
                <th className="px-4 py-3 font-medium">设备ID</th>
                <th className="px-4 py-3 font-medium">时间</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-text-secondary">
                    暂无数据
                  </td>
                </tr>
              ) : (
                items.map(item => (
                  <tr
                    key={item.id}
                    className="border-b border-border last:border-0 hover:bg-bg-hover transition-colors"
                  >
                    <td className="px-4 py-3">
                      {item.user_id ? (
                        <button
                          className="text-brand hover:underline font-mono text-xs"
                          onClick={() => navigate(`/ai/users/${item.user_id}`)}
                        >
                          {item.user_id}
                        </button>
                      ) : (
                        <span className="text-text-tertiary text-xs">匿名</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${eventBadgeClass(item.event_name)}`}
                      >
                        {item.event_label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-text-secondary text-xs max-w-xs truncate">
                      {formatProps(item.properties)}
                    </td>
                    <td className="px-4 py-3">
                      {item.platform ? (
                        <span className="flex items-center gap-1 text-text-secondary">
                          <i
                            className={`${PLATFORM_ICON[item.platform.toLowerCase()] ?? 'ri-device-line'} text-base`}
                          />
                          {item.platform}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-text-secondary">
                      {item.device_id ? `${item.device_id.slice(0, 12)}…` : '-'}
                    </td>
                    <td className="px-4 py-3 text-text-secondary text-xs whitespace-nowrap">
                      {formatTime(item.timestamp)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => fetchData(page - 1, filterUserId, filterEvent, filterFrom, filterTo)}
            className="px-3 py-1.5 border border-border rounded-lg text-sm disabled:opacity-40 hover:bg-bg-hover"
          >
            上一页
          </button>
          <span className="text-sm text-text-secondary">
            第 {page} / {totalPages} 页
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => fetchData(page + 1, filterUserId, filterEvent, filterFrom, filterTo)}
            className="px-3 py-1.5 border border-border rounded-lg text-sm disabled:opacity-40 hover:bg-bg-hover"
          >
            下一页
          </button>
        </div>
      )}
    </div>
  )
}
